/**
 * The online maps this device has seen on the server, kept on the device
 * (docs/plans/2026-09-29-attachments-move-with-their-map.md, stage 3). A map
 * this device knows was online that the server no longer has was moved to
 * another device, or deleted there: this device's copy must not create it
 * online again (the server refuses it: `known` in `POST /api/docs`), and is
 * removed after telling the user.
 *
 * Learned when a map is opened and at each vault load (the server lists the
 * maps it has), with the account it belongs to: at the next vault load, a map
 * of that account that left the vault meanwhile is checked too (its copy
 * here was never removed), and another account's maps are never touched.
 * Where the list is lost (a browser that cleared its storage), the device
 * simply learns again.
 */
const KEY = 'orbital-known-online';

/** docId → { title: when last seen (to name it once gone), account?: user ID } */
function read() {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) || '{}');
    if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
    for (const [docId, entry] of Object.entries(value)) {
      if (!entry || typeof entry !== 'object') value[docId] = { title: typeof entry === 'string' ? entry : '' };
    }
    return value;
  } catch {
    return {};
  }
}

function write(maps) {
  try { localStorage.setItem(KEY, JSON.stringify(maps)); } catch { /* not remembered: learned again */ }
}

/**
 * @param {Array<{docId: string, title?: string}>} maps seen on the server
 * @param {string} [account] the account they belong to (kept if not given)
 */
export function markKnownOnline(maps, account) {
  const known = read();
  let changed = false;
  for (const { docId, title = '' } of maps) {
    if (!docId) continue;
    const before = known[docId];
    const after = { title: title || before?.title || '', ...((account || before?.account) && { account: account || before.account }) };
    if (before && before.title === after.title && before.account === after.account) continue;
    known[docId] = after;
    changed = true;
  }
  if (changed) write(known);
}

/** This account's maps known online that are not among `docIds` (they left its vault). */
export function knownOnlineElsewhere(account, docIds) {
  const listed = new Set(docIds);
  return Object.entries(read())
    .filter(([docId, entry]) => account && entry.account === account && !listed.has(docId))
    .map(([docId]) => docId);
}

export function isKnownOnline(docId) {
  return !!docId && docId in read();
}

/** Its entry ({title, account}), or null: to put back with markKnownOnline. */
export function knownEntry(docId) {
  return read()[docId] || null;
}

/** The title it had when last seen online, or ''. */
export function knownTitle(docId) {
  return read()[docId]?.title || '';
}

/**
 * Signing out: the account's maps stay known (by id: the device must still
 * not create them online again), their titles go (account data, kept
 * readable on the device; learned again at the next vault load).
 * @param {{userId?: string, docIds?: string[]}} scope the account signing out
 */
export function forgetKnownTitles({ userId, docIds = [] } = {}) {
  const ids = new Set(docIds);
  const known = read();
  let changed = false;
  for (const [docId, entry] of Object.entries(known)) {
    if (!entry.title || !((userId && entry.account === userId) || ids.has(docId))) continue;
    entry.title = '';
    changed = true;
  }
  if (changed) write(known);
}

export function forgetKnownOnline(docId) {
  const known = read();
  if (!(docId in known)) return;
  delete known[docId];
  write(known);
}

/**
 * Whether the server still has a map this device knew online: true, false
 * (gone: moved to another device, or deleted there), or null (no answer).
 * Asks as opening it would (`known`: nothing is ever created).
 */
export async function stillOnline(docId) {
  try {
    const response = await fetch('/api/docs', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ doc_id: docId, known: true }),
    });
    if (response.status === 410) return false;
    return response.ok ? true : null;
  } catch {
    return null;
  }
}
